// Core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';

// Component dependencies.
import OverlayInterface from '../../Overlay/index.js';
import { Button, HybridButton } from '../../../../common/components/buttons.jsx';

// Misc dependencies.
import { capitalize, tokenize, updateQueryString, getLabel } from '../../../../common/utils/string.js';
import * as assetUtils from '../../../../common/utils/asset.js';

// Actions.
import Actions from '../actions.js';

// Selectors.
import Selectors from '../selectors.js';

// Config.
import Config from '../config/config.js';

// Model entities.
import DisplayItem from '../../../../common/model/entity/DisplayItem/DisplayItem.js';

/**
 * <pre>
 * Sharing React component.
 *
 * Summary of responsibilities:
 *
 *  - smart component (connected to Redux store).
 *  - composes child components as required to output Sharing elements of a display item.
 *  - handles events received from child components and calls mapped dispatch functions as required.
 *
 * </pre>
 *
 * @author Tim Loram
 *
 * @docPreview {namedExport: "docPreviewExport"}
 *
 * @memberof module:Sharing.Components
 * @namespace
 *
 * @return {Sharing.Components.Sharing}
 */
class Sharing extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Initial state.
    this.state = {
      showOverlay: false,
    };

    // Bind methods.
    this.handleShareClicked = this.handleShareClicked.bind(this);
    this.handleShowShareOverlay = this.handleShowShareOverlay.bind(this);
    this.handleCloseShareOverlay = this.handleCloseShareOverlay.bind(this);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Lifecycle methods */

  /**
   * Called when component mounts for the first time.
   */
  componentDidMount() {}

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:Sharing.Components.Sharing}
   */
  render() {
    let _sharing;
    let _overlay;

    // Render the sharing element appropriately.
    const renderMode = this.props.config.render ? this.props.config.render : 'inline';
    switch (renderMode) {
      // Render sharing icons inline.
      case 'mode.inline':
        _sharing = this.generateShareActions(this.props.item, this.props.keys, this.props.config.style);

        break;

      // Render the sharing element as a single icon that when clicked, opens an overlay with the network share actions inside.
      case 'mode.overlay':
        // Merge in any additional actions given to us.
        let _additionalActions;
        if (this.props.additionalActions) {
          _additionalActions = this.props.additionalActions;
        }
        const _ariaLabel = `${_get(NGX.lang.buttons, 'shareTo')}`;
        _sharing = (
          <ul className="xToolbar xIconToolbar">
            <li className="xAction xActionShareModal">
              <HybridButton
                onClick={this.handleShowShareOverlay}
                className="xInviteIcon xShareModal"
                iconClass="xIcon xIconShareModal"
                ariaLabel={_ariaLabel}
              />
            </li>
            {_additionalActions}
          </ul>
        );

        // Show the overlay if required.
        if (this.state.showOverlay) {
          const _actions = this.generateShareActions(
            this.props.item,
            this.props.keys,
            this.props.config.style,
            this.props.onExternalLinkClick
          );
          const _overlayContents = this.generateOverlayContents(_actions);
          _overlay = (
            <OverlayInterface.Components.Overlay
              closeHandler={this.handleCloseShareOverlay}
              classes="xOverlay-shareModal"
            >
              {_overlayContents}
            </OverlayInterface.Components.Overlay>
          );
        }

        break;

      // Render just the overlay contents directly.
      case 'mode.overlay.content':
        const _actions = this.generateShareActions(
          this.props.item,
          this.props.keys,
          this.props.config.style,
          this.props.onExternalLinkClick
        );
        _overlay = this.generateOverlayContents(_actions);

        break;
    }

    return (
      <div>
        {_sharing}
        {_overlay}
      </div>
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Event handler methods */

  /**
   * Handler called when share action (button/icon) is clicked.
   *
   * @param  {Integer} id       The DisplayItem id.
   * @param  {String} network   The social network.
   * @param  {Object} shareData Object containing additional share properties (ie any data-ngx attributes present on the element)
   * @param  {Object} event     The click event object.
   */
  handleShareClicked(id, network, shareData, event) {
    event.preventDefault();
    event.stopPropagation();

    // Dispatch share action.
    this.props.dispatch.share(id, network, shareData);
  }

  /**
   * Handler called when the generic share icon is clicked and render mode requires an overlay.
   *
   * @param  {Object} event     The click event object.
   */
  handleShowShareOverlay(event) {
    event.preventDefault();
    event.stopPropagation();

    // Update internal state (no need for this to be in Redux)
    this.setState({ showOverlay: true });
  }

  /**
   * Handler called when the overlay close button/icon is clicked.
   *
   */
  handleCloseShareOverlay() {
    // Update internal state (no need for this to be in Redux)
    this.setState({ showOverlay: false });
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Utility methods */

  /**
   * Utility method to generate share actions (as either buttons or icons depending on config style defined).
   *
   * @param  {Object} item The DisplayItem object.
   * @param  {Array} keys   The array of sharing actions to output (contains labels, default message text, network info etc)
   * @param  {String} style String indicating the render style (icon|button).
   * @return {ReactElement} The generated sharing actions element.
   */
  generateShareActions(item, keys, style) {
    let _actionToolbar;
    let _actions;

    const _style = capitalize(this.getSupportedStyle(style));

    // Loop over actions/keys and render.
    _actions = keys.map((key, index) => {
      // Get all the data required to share.
      const _shareData = {
        action:              'share:' + key.context + ':' + key.network,
        id:                  item.id,
        title:               this.parseShareAttr(key.title, item.content),
        byline:              this.parseShareAttr(key.byline, item.content),
        message:             this.parseShareAttr(key.message, item.content),
        picture:             this.parseSharePicture(item),
        link:                this.parseShareLink(key.link, item),
        linktype:            item.meta.network == 'ugc' ? 'internal' : 'external',
        onExternalLinkClick: this.props.onExternalLinkClick,
      };

      // Twitter shares need to include the card type.
      if (key.network && key.network === 'twitter') {
        _shareData.card = 'summary';
      }

      // Map to data attributes.
      const _dataAttr = {};
      Object.keys(_shareData).map(function(key) {
        _dataAttr['data-ngx-' + key.toLowerCase()] = _shareData[key];
      });

      // Define classes.
      const _liClasses = 'xAction xAction' + _style;
      const _aClasses = 'x' + _style + ' x' + capitalize(key.action) + _style;

      // Aria label
      const _ariaLabel = `${_get(NGX.lang.actions, 'share.prefix')} ${key.network}`;

      // Bind the current data object to the click handler.
      let _shareClickHandler = this.handleShareClicked.bind(this, item.id, key.network, _shareData);
      _shareClickHandler = this.props.onExternalLinkClick ?
        this.props.onExternalLinkClick.bind(this, _shareClickHandler) :
        _shareClickHandler;

      const onClick = function(network, e) {
        if (this.props.onClick) {
          this.props.onClick(network, e);
        }
        _shareClickHandler(e);
      }.bind(this, key.network);

      let _button;
      switch (_style) {
        // Icon style buttons.
        case 'Icon':
          const _iClasses = 'x' + _style + ' x' + _style + capitalize(key.action) + capitalize(key.network);
          _button = (
            <HybridButton
              onClick={onClick}
              className={_aClasses}
              iconClass={_iClasses}
              dataAttributes={_dataAttr}
              ariaLabel={_ariaLabel}
            />
          );

          break;

        // Button style buttons.
        case 'Button':
          _button = (
            <Button onClick={onClick} className={_aClasses} dataAttributes={_dataAttr} ariaLabel={_ariaLabel}>
              {capitalize(key.btnText)}
            </Button>
          );

          break;
      }

      return (
        <li key={index} className={_liClasses}>
          {_button}
        </li>
      );
    });

    const _ulClasses = 'xToolbar x' + _style + 'Toolbar xToolbarSharing';

    // Wrap actions in toolbar element.
    _actionToolbar = <ul className={_ulClasses}>{_actions}</ul>;

    return _actionToolbar;
  }

  /**
   * Utility method to render the generated sharing actions element inside an Overlay component.
   *
   * @param  {ReactElement} actions The generated sharing actions element.
   * @return {ReactElement} The generated overlay contents element.
   */
  generateOverlayContents(actions) {
    const _overlayContents = (
      <div>
        <h1>{getLabel('share.label')}</h1>
        <div className="xActionContainer xActionSharing xFixed">{actions}</div>
      </div>
    );

    return _overlayContents;
  }

  /**
   * Utility method which attempts to parse the correct attribute to share from the DisplayItem.
   *
   * @param  {String} needle    The key/token to search for.
   * @param  {Object} haystack  The DisplayItem > Content object.
   * @return {String} The parsed and generated string.
   */
  parseShareAttr(needle, haystack) {
    let tokenizedString = '';

    // Call tokenize method to search and replace placeholder values with actual values of item.
    tokenizedString = tokenize(needle, haystack).replace(/(<([^>]+)>)/gi, '');

    return tokenizedString;
  }

  /**
   * Utility method which attempts to parse the correct picture asset to share from the DisplayItem.
   *
   * @param  {Object} item  The DisplayItem.
   * @return {String} The parsed image.
   */
  parseSharePicture(item) {
    const assets = item.assets;
    if (assets) {
      // If we have a main asset.
      if (assets.main) {
        var assetObj = assetUtils.assetFromRef(assets.main);

        if (assetObj.assetType === 'video' && assets.thumbnail) {
          if (assetObj.type === 'mux') {
            var thumbAssetObj = assetUtils.assetFromRef(assets.thumbnail);
            return assetUtils.getMuxThumbnail(thumbAssetObj);
          }
          return assets.thumbnail;
        } else {
          if (assetObj.type === 'mux') {
            return assetUtils.getMuxThumbnail(assetObj);
          }
          return assets.main;
        }

        // Otherwise try and fallback to thumbnail asset.
      } else if (assets.thumbnail) {
        return assets.thumbnail;
      }
    }

    return '';
  }

  /**
   * Utility method which attempts to parse/generate the correct external link to share from the DisplayItem.
   *
   * @param  {String} defaultLink Default fallback link.
   * @param  {Object} item  The DisplayItem.
   * @return {String} The parsed link.
   */
  parseShareLink(defaultLink, item) {
    if (
      item.ref.externalServiceRef !== 'external.service.ugc' &&
      item.ref.externalServiceRef !== 'external.service.seeded'
    ) {
      return item.content.link;
    } else {
      var applyRoute = ['ngx.me', 'engagesciences.com:8443/link/', 'engageplatform.com/link/'],
        method = 'ext';

      // Check smart url first
      _forEach(applyRoute, function(domain) {
        if (defaultLink.indexOf(domain) > -1) {
          method = 'route';
          defaultLink = defaultLink + '/${id}';
        }
      });
      if (method !== 'route') {
        // Query string is now added to any non-route URL's
        method = 'qs';
        defaultLink = updateQueryString({ ngxItemID: '${id}' }, true, defaultLink);
      }

      return tokenize(defaultLink, item);
    }
  }

  /**
   * Utility method to check if the requested style of share element based on config is supported.
   *
   * @param  {String} style The requested style.
   * @return {String} The share element style
   */
  getSupportedStyle(style) {
    let _style = 'icon';

    if (['icon', 'button'].indexOf(style) !== -1) {
      _style = style;
    }

    return _style;
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item The DisplayItem being shared.
 * @property {Object} config Object containing config information.
 * @property {Array} keys Array containing the sharing action objects for each social network.
 * @property {Object} actions Object containing the sharing action objects for each social network.
 * @property {ReactElement} additionalActions Additional element to append after the current action elements.
 * @property {Array} shares Array of previously shared DisplayItem ids and corresponding social network info.
 * @property {Function} onExternalLinkClick Callback for external link clicks
 * @property {Function} onClick Callback clicking on the share buttons
 */
Sharing.propTypes = {
  item:                PropTypes.instanceOf(DisplayItem),
  config:              PropTypes.object,
  keys:                PropTypes.array,
  actions:             PropTypes.object,
  additionalActions:   PropTypes.element,
  shares:              PropTypes.array,
  onExternalLinkClick: PropTypes.func,
  onClick:             PropTypes.func,
};

/**
 * React default props.
 *
 * @type {Object}
 */
Sharing.defaultProps = {};

/**
 * Maps Redux state values to React component props. Called automatically whenever
 * the bound state changes.
 *
 * @private
 *
 * @param  {Object} state The Redux state.
 * @param  {Object} props The current React component props.
 * @return {Object} Updated React component props.
 */
const mapStateToProps = function(state, ownProps) {
  return {
    shares: Selectors.getShares(state),
  };
};

/**
 * Binds Redux action dispatches to React component props.
 *
 * @private
 *
 * @param  {Function} dispatch Dispatch context.
 * @return {Object} The mapped functions.
 */
const mapDispatchToProps = function(dispatch) {
  return {
    dispatch: {
      share: function(id, network, shareData) {
        dispatch(Actions.share(id, network, shareData));
      },
    },
  };
};

// Generate connected/smart component.
const SharingConnected = connect(mapStateToProps, mapDispatchToProps)(Sharing);

// Export the connected/smart component as default.
export default SharingConnected;

// Also export the dumb component (for unit testing, doc preview etc).
export { Sharing as docPreviewExport };
